import type vtkRenderer from '@kitware/vtk.js/Rendering/Core/Renderer';
import type vtkRenderWindow from '@kitware/vtk.js/Rendering/Core/RenderWindow';
import type vtkRenderWindowInteractor from '@kitware/vtk.js/Rendering/Core/RenderWindowInteractor';
// import type vtkOpenGLHardwareSelector from '@kitware/vtk.js/Rendering/OpenGL/HardwareSelector';
import type vtkOpenGLRenderWindow from '@kitware/vtk.js/Rendering/OpenGL/RenderWindow';

// Imported from the leaf module, not the '../display' barrel: the barrel
// re-exports the display objects, which import the viewports back — pulling it
// in here makes ViewportType undefined for whoever enters the graph first.
import { DisplayFeatureType, IDisplayObject, ViewMode } from '../display/types';
import { RGB, Vec3 } from '../domain';
import { OverlayLayer } from './overlay-layer';
import type { Color3DPresetId } from '../services/color-3d-presets';

/** Options passed at viewport creation time, common to every viewport type. */
export interface ViewportOptions {
    /** Whether to attach an orientation marker widget to this viewport. */
    showOrientationWidget?: boolean;
    backgroundColor?: RGB;
    ignoreEvent?: boolean;
}

/** Creation options specific to 3D volume viewports (VolumeViewport3D). */
export interface VolumeViewportOptions extends ViewportOptions {
    /**
     * Pin the 3D color preset for this viewport: it renders with this profile
     * and ignores Color3DService's active preset.
     */
    color3DPresetId?: Color3DPresetId;
}

/**
 * The creation options of any viewport type — what generic plumbing
 * (ViewportService, ViewportLayoutService) accepts and forwards.
 *
 * Each viewport subclass narrows this to the set it understands: 2D viewports to
 * `ViewportOptions`, VolumeViewport3D to `VolumeViewportOptions`. Type-specific
 * members are therefore accepted anywhere and simply ignored by viewports that
 * don't read them; unknown members are still a compile error.
 */
export type AnyViewportOptions = ViewportOptions | VolumeViewportOptions;

/** VTK objects created for each viewport by ViewportService */
export interface ViewportVtkContext {
    readonly childRenderWindow: vtkRenderWindow;
    readonly childView: vtkOpenGLRenderWindow;
    readonly interactor: vtkRenderWindowInteractor;
    readonly container: HTMLElement;

    /**
     * Requests a render for this viewport
     * Debounced at ViewportService level. Multiple calls batched in a single animation frame
     */
    render: () => void;
}

/**
 * Bounds applied to an orthographic camera's parallelScale — half the visible
 * viewport height in world millimetres. Smaller means more magnified.
 */
export interface ParallelScaleRange {
    min: number;
    max: number;
}

export interface IViewport {
    readonly id: string;
    readonly type: ViewportType;
    readonly container: HTMLElement;

    /** Resize to match container dimensions */
    resize(): void;

    /** Trigger a render */
    render(): void;

    /** Trigger an immediate rendering, not debounced */
    forceRender(): void;

    /** Clean up resources */
    dispose(): void;

    /** Get the VTK render window */
    getRenderWindow(): vtkRenderWindow;
    getOpenGLRenderWindow(): vtkOpenGLRenderWindow;

    /** Get the canvas */
    getCanvas(): HTMLCanvasElement | null;

    /** Capture the viewport's rendered image as a data URL. */
    captureImage(format?: 'image/png' | 'image/jpeg', captureOverlay?: boolean): Promise<string | null>;

    //getHardwareSelector(): vtkOpenGLHardwareSelector | null;

    /** Get the VTK renderer */
    getRenderer(layer?: number): vtkRenderer;

    getAllRenderer(): vtkRenderer[];

    canvasToNormalized(canvasX: number, canvasY: number): [number, number];

    /** Convert canvas coordinates to world coordinates */
    canvasToWorld(canvasX: number, canvasY: number, depth?: number): Vec3;

    /** Convert world coordinates to canvas coordinates */
    worldToCanvas(worldPosition: number[]): { x: number; y: number; visible: boolean };

    /** Get the overlay layer for this viewport */
    getOverlayLayer(): OverlayLayer;

    /** Get current camera state (for change detection) */
    getCameraState(): CameraState;

    /** Apply a full camera state to the viewport */
    setCameraState(state: CameraState): void;

    /** Update the camera clipping range for the active renderer */
    updateCameraClippingRange(): void;

    /**
     * Allowed orthographic parallelScale range for this viewport,
     * or null when zoom is unbounded.
     */
    getParallelScaleRange(): ParallelScaleRange | null;

    findDisplayObjectsByFeatureType(type: DisplayFeatureType): IDisplayObject[];

    /** Force all display objects to re-run their `update()` cycle. */
    refreshDisplayObjects(): void;
}

export enum ViewportType {
    VOLUME_3D = 'volume_3d',
    SLICE_AXIAL = 'slice_axial',
    SLICE_SAGITTAL = 'slice_sagittal',
    SLICE_CORONAL = 'slice_coronal',
    SLICE_OBLIQUE = 'slice_oblique',
    CPR_STRETCHED = 'cpr_stretched',
    CPR_STRAIGHTENED = 'cpr_straightened',
}

export const VIEW_MODE_TO_VIEWPORT_TYPE: Record<ViewMode, ViewportType> = {
    [ViewMode.VIEW_3D]: ViewportType.VOLUME_3D,
    [ViewMode.VIEW_2D_AXIAL]: ViewportType.SLICE_AXIAL,
    [ViewMode.VIEW_2D_SAGITTAL]: ViewportType.SLICE_SAGITTAL,
    [ViewMode.VIEW_2D_CORONAL]: ViewportType.SLICE_CORONAL,
    [ViewMode.VIEW_2D_OBLIQUE]: ViewportType.SLICE_OBLIQUE,
    [ViewMode.VIEW_CPR_STRETCHED]: ViewportType.CPR_STRETCHED,
    [ViewMode.VIEW_CPR_STRAIGHTENED]: ViewportType.CPR_STRAIGHTENED,
};

export const VIEWPORT_TYPE_TO_VIEW_MODE = Object.fromEntries(
    Object.entries(VIEW_MODE_TO_VIEWPORT_TYPE).map(([vm, vt]) => [vt, vm])
) as Record<ViewportType, ViewMode>;

export enum CameraBehaviour {
    ORTHOGRAPHIC = 'orthographic',
    PERSPECTIVE = 'perspective',
}

export interface SnapshotOptions {
    mimeType?: 'image/png' | 'image/jpeg';
    /** Quality 0–1 for JPEG. Ignored for PNG. */
    quality?: number;
}

export interface CameraState {
    position: Vec3;
    focalPoint: Vec3;
    viewUp: Vec3;
    viewAngle: number; // For perspective
    parallelScale: number; // For orthographic
}
